import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  CREDIT_PACKS,
  findPack,
  buildCustomPack,
  MIN_CREDIT_SPEND_ZAR,
  PRICE_PER_CREDIT_ZAR,
  ADMIN_FEE_ZAR,
} from "@/lib/credit-packs";
import { assertAdmin } from "@/lib/api/_admin-guard";
import { grantCredits } from "@/lib/api/_credits";

function makeRef() {
  const stamp = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `NDL-CR-${stamp}-${rand}`;
}

// USER: view own balance + recent ledger
export const getMyCreditState = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: bal } = await context.supabase
      .from("user_credits")
      .select("balance")
      .eq("user_id", context.userId)
      .maybeSingle();
    const { data: ledger } = await context.supabase
      .from("credit_ledger")
      .select("delta, reason, ref, created_at")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    const { data: orders } = await context.supabase
      .from("credit_orders")
      .select("id, pack_code, credits, amount_zar, reference, status, created_at, paid_at, payment_method, paid_provider_ref, receipt_sent_at, public_token")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(500);

    return {
      balance: (bal?.balance as number | undefined) ?? 0,
      ledger: ledger ?? [],
      orders: orders ?? [],
    };
  });

// USER: order a credit pack — returns reference + redirect URL.
// Supports either a preset pack (`packCode`) OR a custom top-up
// (`customCredits` — server enforces the R20 minimum + R5 admin fee).
export const orderCreditPack = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      packCode: z.string().min(1).max(40).optional(),
      customCredits: z.number().int().positive().max(100000).optional(),
    }).refine((v) => v.packCode || v.customCredits, {
      message: "Provide packCode or customCredits",
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    let pack;
    const minCredits = Math.ceil(MIN_CREDIT_SPEND_ZAR / PRICE_PER_CREDIT_ZAR);
    if (data.customCredits) {
      if (data.customCredits < minCredits) {
        throw new Error(
          `Minimum top-up is ${minCredits} credits (R${MIN_CREDIT_SPEND_ZAR}). A R${ADMIN_FEE_ZAR} admin fee is added per top-up.`,
        );
      }
      pack = buildCustomPack(data.customCredits);
    } else {
      pack = findPack(data.packCode!);
      if (!pack) throw new Error("Unknown pack");
    }
    // Belt-and-braces server-side invariants — never trust the client.
    const expectedCreditsValue =
      Math.round(pack.credits * PRICE_PER_CREDIT_ZAR * 100) / 100;
    const expectedTotal = Math.round((expectedCreditsValue + ADMIN_FEE_ZAR) * 100) / 100;
    if (pack.credits < minCredits) {
      throw new Error(`Top-up below minimum (${minCredits} credits).`);
    }
    if (Math.abs(pack.amountZar - expectedTotal) > 0.01) {
      throw new Error("Pack pricing mismatch — admin fee missing.");
    }
    if (pack.adminFeeZar !== ADMIN_FEE_ZAR) {
      throw new Error("Admin fee mismatch.");
    }
    if (pack.amountZar < MIN_CREDIT_SPEND_ZAR + ADMIN_FEE_ZAR - 0.01) {
      throw new Error(
        `Minimum charge is R${(MIN_CREDIT_SPEND_ZAR + ADMIN_FEE_ZAR).toFixed(2)} (includes R${ADMIN_FEE_ZAR} admin fee).`,
      );
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const reference = makeRef();
    const { data: row, error } = await supabaseAdmin
      .from("credit_orders")
      .insert({
        user_id: context.userId,
        pack_code: pack.code,
        credits: pack.credits,
        amount_zar: pack.amountZar,
        reference,
        status: "pending",
      })
      .select("id, reference")
      .single();
    if (error) throw new Error(error.message);

    // Proforma invoice email intentionally NOT sent on order creation.
    // The admin emails the official invoice after confirming the order
    // service, so customers go straight to PayFast checkout instead.

    return { reference: row.reference, redirect: `/invoice/${row.reference}` };
  });

// AUTHENTICATED: look up an invoice by reference (credit / cv / contract).
// Requires the caller to be the order owner (matching user_id, or matching
// verified email for contract orders) or a site admin. This prevents anyone
// who knows or guesses a short reference from reading buyer PII or getting
// the paid contract download token.
export const getInvoiceByReference = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ reference: z.string().min(4).max(80) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    const { data: viewerProfile } = await supabaseAdmin
      .from("profiles")
      .select("email")
      .eq("id", context.userId)
      .maybeSingle();
    const viewerEmail = (viewerProfile?.email ?? "").toLowerCase();

    // Credit order?
    const { data: credit } = await supabaseAdmin
      .from("credit_orders")
      .select(
        "id, user_id, pack_code, credits, amount_zar, reference, status, created_at, paid_at, payment_method, paid_provider_ref, receipt_sent_at, receipt_email_to, public_token",
      )
      .eq("reference", data.reference)
      .maybeSingle();
    if (credit) {
      if (!isAdmin && credit.user_id !== context.userId) {
        throw new Error("Order not found");
      }
      const pack = findPack(credit.pack_code);
      return {
        kind: "credit" as const,
        order: {
          reference: credit.reference,
          title: `${pack?.label ?? credit.pack_code} pack — ${credit.credits} credits`,
          amountZar: Number(credit.amount_zar),
          status: credit.status,
          createdAt: credit.created_at,
          paidAt: credit.paid_at,
          paymentMethod: credit.payment_method ?? null,
          paidProviderRef: credit.paid_provider_ref ?? null,
          receiptSentAt: credit.receipt_sent_at ?? null,
          receiptEmailTo: credit.receipt_email_to ?? null,
          publicToken: (credit as any).public_token ?? null,
          lineItems: [
            { label: `${credit.credits} AI credits`, qty: 1, amount: Number(credit.amount_zar) },
          ],
        },
      };
    }
    // CV order?
    const { data: cv } = await supabaseAdmin
      .from("cv_orders" as any)
      .select(
        "user_id, reference, quantity, unit_price_zar, amount_zar, status, created_at, paid_at, payment_method, paid_provider_ref, receipt_sent_at, receipt_email_to, public_token",
      )
      .eq("reference", data.reference)
      .maybeSingle();
    if (cv) {
      if (!isAdmin && (cv as any).user_id !== context.userId) {
        throw new Error("Order not found");
      }
      const q = Number((cv as any).quantity);
      const unit = Number((cv as any).unit_price_zar);
      return {
        kind: "cv" as const,
        order: {
          reference: (cv as any).reference,
          title: `${q} × AI CV generation (R${unit.toFixed(2)} each)`,
          amountZar: Number((cv as any).amount_zar),
          status: (cv as any).status,
          createdAt: (cv as any).created_at,
          paidAt: (cv as any).paid_at,
          paymentMethod: (cv as any).payment_method ?? null,
          paidProviderRef: (cv as any).paid_provider_ref ?? null,
          receiptSentAt: (cv as any).receipt_sent_at ?? null,
          receiptEmailTo: (cv as any).receipt_email_to ?? null,
          publicToken: (cv as any).public_token ?? null,
          quantity: q,
          unitPriceZar: unit,
          lineItems: [
            {
              label: `${q} AI CV credit${q === 1 ? "" : "s"}`,
              qty: q,
              amount: Number((cv as any).amount_zar),
            },
          ],
        },
      };
    }
    // Contract order?
    const { data: contract } = await supabaseAdmin
      .from("contract_orders")
      .select(
        "reference, title, slug, amount_zar, status, created_at, paid_at, client_name, client_email, payment_method, paid_provider_ref, receipt_sent_at, download_token",
      )
      .eq("reference", data.reference)
      .maybeSingle();
    if (contract) {
      const contractEmail = (contract.client_email ?? "").toLowerCase();
      const isOwner = viewerEmail && contractEmail && viewerEmail === contractEmail;
      if (!isAdmin && !isOwner) {
        throw new Error("Order not found");
      }
      const isPaid = contract.status === "paid";
      return {
        kind: "contract" as const,
        order: {
          reference: contract.reference,
          title: contract.title,
          slug: contract.slug,
          amountZar: Number(contract.amount_zar),
          status: contract.status,
          createdAt: contract.created_at,
          paidAt: contract.paid_at,
          clientName: contract.client_name,
          paymentMethod: contract.payment_method ?? null,
          paidProviderRef: contract.paid_provider_ref ?? null,
          receiptSentAt: contract.receipt_sent_at ?? null,
          receiptEmailTo: null as string | null,
          // Download token doubles as the public_token for contract receipts —
          // callers use it as ?t=<token> on /api/public/receipt.
          publicToken: contract.download_token ?? null,
          // Download token is only exposed to the verified owner (or admin)
          // after the PayFast webhook confirms the payment.
          downloadToken: isPaid ? contract.download_token : null,
          lineItems: [
            { label: contract.title, qty: 1, amount: Number(contract.amount_zar) },
          ],
        },
      };
    }
    throw new Error("Order not found");
  });






// ADMIN: list all credit orders + tell us whether the credits were actually
// granted to the customer's ledger. `granted` is true iff a `credit_ledger`
// row exists for (reason='pack_purchase', ref=reference). Use this to spot
// paid-but-unprovisioned orders that need a one-click "Provision credits".
export const adminListCreditOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: orders, error } = await supabaseAdmin
      .from("credit_orders")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) throw new Error(error.message);
    const userIds = Array.from(new Set((orders ?? []).map((o) => o.user_id)));
    const refs = (orders ?? []).map((o) => o.reference).filter(Boolean);
    const profilesMap: Record<string, { email: string; full_name: string }> = {};
    if (userIds.length) {
      const { data: profiles } = await supabaseAdmin
        .from("profiles")
        .select("id, email, full_name")
        .in("id", userIds);
      for (const p of profiles ?? []) {
        profilesMap[p.id] = { email: p.email ?? "", full_name: p.full_name ?? "" };
      }
    }
    const grantedRefs = new Set<string>();
    if (refs.length) {
      const { data: ledger } = await supabaseAdmin
        .from("credit_ledger")
        .select("ref")
        .eq("reason", "pack_purchase")
        .in("ref", refs);
      for (const row of ledger ?? []) {
        if ((row as any).ref) grantedRefs.add((row as any).ref);
      }
    }
    return {
      orders: (orders ?? []).map((o) => ({
        ...o,
        user: profilesMap[o.user_id] ?? null,
        granted: grantedRefs.has(o.reference),
      })),
    };
  });


// ADMIN: mark a credit order paid → grants credits via RPC (idempotent on ref)
export const adminMarkCreditOrderPaid = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      id: z.string().uuid(),
      status: z.enum(["pending", "paid", "cancelled"]),
      adminNotes: z.string().max(2000).optional(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: order, error: getErr } = await supabaseAdmin
      .from("credit_orders")
      .select("*")
      .eq("id", data.id)
      .single();
    if (getErr || !order) throw new Error("Order not found");

    const isPaid = data.status === "paid";
    const { error: upErr } = await supabaseAdmin
      .from("credit_orders")
      .update({
        status: data.status,
        admin_notes: data.adminNotes ?? null,
        paid_at: isPaid ? new Date().toISOString() : null,
        paid_by: isPaid ? context.userId : null,
      })
      .eq("id", data.id);
    if (upErr) throw new Error(upErr.message);

    if (isPaid) {
      // Grant credits idempotently via reference
      await grantCredits(order.user_id, order.credits, "pack_purchase", order.reference);
    }
    return { ok: true };
  });


// ADMIN: force-provision credits for an order whose PayFast ITN never
// completed the grant step. Safe to click any time — `grant_credits` is
// atomically idempotent on (reason='pack_purchase', ref=reference), so
// repeated clicks never double-credit. If the order is still `pending`
// we also flip it to `paid` so the customer-facing UI matches reality.
export const adminProvisionCreditOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      id: z.string().uuid(),
      adminNotes: z.string().max(2000).optional(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: order, error: getErr } = await supabaseAdmin
      .from("credit_orders")
      .select("id, user_id, credits, reference, status, paid_at")
      .eq("id", data.id)
      .single();
    if (getErr || !order) throw new Error("Order not found");

    // Capture pre/post balance so we can tell the admin whether this call
    // actually issued new credits or hit the idempotency guard.
    const { data: priorBal } = await supabaseAdmin
      .from("user_credits")
      .select("balance")
      .eq("user_id", order.user_id)
      .maybeSingle();
    const before = (priorBal?.balance as number | undefined) ?? 0;

    const newBalance = await grantCredits(
      order.user_id,
      order.credits,
      "pack_purchase",
      order.reference,
    );

    if (order.status !== "paid") {
      await supabaseAdmin
        .from("credit_orders")
        .update({
          status: "paid",
          paid_at: order.paid_at ?? new Date().toISOString(),
          paid_by: context.userId,
          payment_method: "manual_admin",
          admin_notes:
            data.adminNotes ??
            `Manually provisioned by admin on ${new Date().toISOString()}`,
        })
        .eq("id", order.id);
    }

    const issued = Math.max(0, newBalance - before);
    return {
      ok: true,
      balance: newBalance,
      issued,
      alreadyGranted: issued === 0,
    };
  });

export { CREDIT_PACKS };




// ADMIN: audit credit orders alongside the PayFast ITN events that processed
// them. Joins on `reference` so we can spot duplicate webhook attempts and
// confirm the idempotency guard (`processed=true` + the unique
// `(provider, external_event_id)` row) granted credits exactly once.
export const adminListCreditOrdersAudit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: orders, error } = await supabaseAdmin
      .from("credit_orders")
      .select(
        "id, user_id, pack_code, credits, amount_zar, reference, status, paid_at, paid_provider_ref, payment_method, receipt_sent_at, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);

    const refs = (orders ?? []).map((o) => o.reference).filter(Boolean);
    const userIds = Array.from(new Set((orders ?? []).map((o) => o.user_id)));

    const [{ data: events }, { data: profiles }, { data: ledger }] = await Promise.all([
      refs.length
        ? supabaseAdmin
            .from("payment_events")
            .select(
              "id, provider, external_event_id, event_type, reference, processed, processed_at, error, error_message, created_at, payload",
            )
            .in("reference", refs)
            .order("created_at", { ascending: false })
        : Promise.resolve({ data: [] as any[] }),
      userIds.length
        ? supabaseAdmin.from("profiles").select("id, email, full_name").in("id", userIds)
        : Promise.resolve({ data: [] as any[] }),
      refs.length
        ? supabaseAdmin
            .from("credit_ledger")
            .select("user_id, delta, reason, ref, created_at")
            .eq("reason", "pack_purchase")
            .in("ref", refs)
        : Promise.resolve({ data: [] as any[] }),
    ]);

    const profileMap: Record<string, { email: string; full_name: string }> = {};
    for (const p of profiles ?? []) {
      profileMap[(p as any).id] = {
        email: (p as any).email ?? "",
        full_name: (p as any).full_name ?? "",
      };
    }

    const eventsByRef: Record<string, any[]> = {};
    for (const e of (events ?? []) as any[]) {
      if (!e.reference) continue;
      (eventsByRef[e.reference] ||= []).push(e);
    }

    const ledgerByRef: Record<string, any> = {};
    for (const l of (ledger ?? []) as any[]) {
      if (!l.ref) continue;
      // Keep the first grant per ref (idempotent reason+ref means there's
      // only ever one positive row, but be defensive).
      if (!ledgerByRef[l.ref]) ledgerByRef[l.ref] = l;
    }

    return {
      rows: (orders ?? []).map((o) => {
        const evs = eventsByRef[o.reference] ?? [];
        const completeEvents = evs.filter(
          (e) =>
            String(e.event_type || "").toLowerCase().includes("complete") ||
            String(e.payload?.payment_status || "").toLowerCase() === "complete",
        );
        const processedOk = evs.some((e) => e.processed === true);
        const failures = evs.filter(
          (e) => e.processed === false && (e.error || e.error_message),
        );
        const grant = ledgerByRef[o.reference] ?? null;
        return {
          ...o,
          amount_zar: Number(o.amount_zar),
          user: profileMap[o.user_id] ?? null,
          events: evs,
          eventCount: evs.length,
          completeEventCount: completeEvents.length,
          duplicateCompletes: Math.max(0, completeEvents.length - 1),
          processedOk,
          failureCount: failures.length,
          grant,
          // Idempotency status surfaces the three things that matter:
          //  - did we receive at least one COMPLETE ITN?
          //  - did the unique (provider, external_event_id) constraint hold?
          //  - did `grant_credits(reason='pack_purchase', ref=reference)` run
          //    exactly once?
          idempotency:
            grant && processedOk
              ? completeEvents.length > 1
                ? "deduped"
                : "ok"
              : evs.length === 0
                ? "no-itn"
                : failures.length > 0
                  ? "failed"
                  : "pending",
        };
      }),
    };
  });


// ADMIN: per-order reconciliation history + current ledger provisioning status.
// Looks for any `credit_reconciliation_runs.details` JSONB entry referencing
// this order's PayFast reference and surfaces the recent ITN attempts so the
// admin has a complete provisioning audit trail in one place.
export const adminGetOrderReconciliation = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ reference: z.string().min(4).max(120) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [{ data: order }, { data: ledger }, { data: itn }] = await Promise.all([
      supabaseAdmin
        .from("credit_orders")
        .select("id, user_id, credits, reference, status, paid_at, created_at")
        .eq("reference", data.reference)
        .maybeSingle(),
      supabaseAdmin
        .from("credit_ledger")
        .select("id, delta, reason, ref, created_at")
        .eq("reason", "pack_purchase")
        .eq("ref", data.reference)
        .order("created_at", { ascending: false }),
      supabaseAdmin
        .from("payfast_itn_log" as any)
        .select(
          "id, pf_payment_id, payment_status, signature_valid, server_validate_result, webhook_outcome, grant_status, grant_error, attempt_no, processing_ms, notes, created_at",
        )
        .eq("reference", data.reference)
        .order("created_at", { ascending: false })
        .limit(20),
    ]);

    // Find reconciliation runs whose JSONB `details` contains this reference.
    const { data: runs } = await supabaseAdmin
      .from("credit_reconciliation_runs")
      .select("id, started_at, finished_at, scanned_count, fixed_count, failed_count, triggered_by, details")
      .filter(
        "details",
        "cs",
        JSON.stringify([{ reference: data.reference }]),
      )
      .order("started_at", { ascending: false })
      .limit(20);

    const history = (runs ?? []).map((r: any) => {
      const entry = Array.isArray(r.details)
        ? r.details.find((d: any) => d?.reference === data.reference)
        : null;
      return {
        run_id: r.id,
        started_at: r.started_at,
        finished_at: r.finished_at,
        triggered_by: r.triggered_by,
        entry: entry ?? null,
      };
    });

    return {
      order: order ?? null,
      ledgerEntries: ledger ?? [],
      itnAttempts: itn ?? [],
      reconciliationHistory: history,
      provisioned: (ledger ?? []).length > 0,
    };
  });


// ADMIN: re-process an unmatched PayFast ITN by replaying the most recent
// logged raw_payload for a given m_payment_id back through our ITN handler.
// The replay path skips IP/signature/duplicate guards (authenticated by the
// CRON_SECRET header) but still goes through grant_credits, which remains
// atomically idempotent on (reason='pack_purchase', ref).
export const adminRetryPayfastItn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      reference: z.string().min(4).max(120),
      // Optional override: replay a specific log row instead of the latest.
      logId: z.string().uuid().optional(),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const q = supabaseAdmin
      .from("payfast_itn_log" as any)
      .select("id, raw_payload, payment_status, pf_payment_id, created_at")
      .eq("reference", data.reference);
    const { data: row, error } = data.logId
      ? await q.eq("id", data.logId).maybeSingle()
      : await q.order("created_at", { ascending: false }).limit(1).maybeSingle();
    if (error) throw new Error(error.message);
    if (!row || !(row as any).raw_payload) {
      throw new Error(
        "No stored ITN payload found for this reference. Replay requires at least one prior webhook attempt.",
      );
    }

    const payload = (row as any).raw_payload as Record<string, string>;
    const body = new URLSearchParams();
    for (const [k, v] of Object.entries(payload)) {
      if (v != null) body.append(k, String(v));
    }

    const cronSecret = (process.env.CRON_SECRET ?? "").trim();
    if (!cronSecret) {
      throw new Error("CRON_SECRET is not configured — cannot authorise replay.");
    }
    const origin =
      process.env.SITE_URL ||
      process.env.PUBLIC_SITE_URL ||
      "https://project--f9ca952a-2109-492f-a7b0-0345b2dfa2e1.lovable.app";

    const res = await fetch(`${origin}/api/public/payfast-itn`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "x-payfast-replay-token": cronSecret,
      },
      body: body.toString(),
    });
    const text = await res.text().catch(() => "");

    return {
      ok: res.ok,
      status: res.status,
      response: text.slice(0, 500),
      replayedLogId: (row as any).id,
    };
  });

